'use client';

import { cn } from '@/app/lib';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { HitsPerPage, Pagination, useInstantSearch } from 'react-instantsearch';
import { TextButton } from '@workspace/ui/common';

const DEFAULT_HITS_PER_PAGE = 9;
const MAX_HITS_FALLBACK = 1000;

type CustomShowMoreProps = {
  hitPerPage?: number;
  incrementBy?: number;
  className?: string;
  classNames?: {
    loadMore?: string;
    loadAll?: string;
  };
  translations?: {
    showMore?: string;
    showAll?: string;
  };
};

export function AlgoliaShowMore({ hitPerPage = DEFAULT_HITS_PER_PAGE, ...props }: CustomShowMoreProps) {
  const items = useMemo(() => {
    return [{ label: '', value: hitPerPage, default: true }];
  }, [hitPerPage]);
  return (
    <>
      <HitsPerPage items={items} className="sr-only" />
      <Pagination className="sr-only" />
      <CustomShowMoreContent {...props} />
    </>
  );
}

const CustomShowMoreContent = (props: Omit<CustomShowMoreProps, 'hitPerPage'>) => {
  const { indexRenderState } = useInstantSearch();
  const trackHitsPerPage = useRef<number>(DEFAULT_HITS_PER_PAGE);

  const defaultHitPerPage = useMemo(() => {
    const items = indexRenderState.hitsPerPage?.items;
    if (!items) return DEFAULT_HITS_PER_PAGE;

    const defaultItem = items.find((item: any) => item.default);
    return defaultItem?.value ?? DEFAULT_HITS_PER_PAGE;
  }, [indexRenderState.hitsPerPage?.items]);

  useEffect(() => {
    trackHitsPerPage.current = defaultHitPerPage;
  }, [defaultHitPerPage]);

  const hasMore = indexRenderState.pagination?.canRefine;

  const step = props.incrementBy ?? defaultHitPerPage;

  const showMore = useCallback(() => {
    const nextHitsCount = trackHitsPerPage.current + step;
    indexRenderState.hitsPerPage?.refine(nextHitsCount);
    trackHitsPerPage.current = nextHitsCount;
  }, [step, indexRenderState.hitsPerPage]);

  const showAll = useCallback(() => {
    const totalHits = indexRenderState.pagination?.nbHits ?? MAX_HITS_FALLBACK;
    indexRenderState.hitsPerPage?.refine(totalHits);
  }, [indexRenderState.hitsPerPage, indexRenderState.pagination?.nbHits]);

  if (!hasMore) return null;

  return (
    <div className={cn('mt-16 flex justify-center gap-8', props.className)}>
      <TextButton className={cn('underline', props.classNames?.loadMore)} onClick={showMore}>
        {props.translations?.showMore || 'Load more'}
      </TextButton>
      <TextButton className={cn('underline', props.classNames?.loadAll)} onClick={showAll}>
        {props.translations?.showAll || 'Show all'}
      </TextButton>
    </div>
  );
};
